/**
 * WeeklyScheduleEditor
 * Edit the recurring weekly time-slot schedule for a Gutachter.
 * Each day stores a JSON string of `[{ start, end }]` windows.
 */
import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { Button, Skeleton } from "@jourfx/ui";

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABELS: Record<string, string> = {
  monday: "Montag",
  tuesday: "Dienstag",
  wednesday: "Mittwoch",
  thursday: "Donnerstag",
  friday: "Freitag",
  saturday: "Samstag",
  sunday: "Sonntag",
};

interface TimeWindow {
  start: string;
  end: string;
}

type Schedule = Record<(typeof DAY_KEYS)[number], TimeWindow[]>;

interface WeeklyScheduleEditorProps {
  gutachterId: string;
}

function emptySchedule(): Schedule {
  return {
    monday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    tuesday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    wednesday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    thursday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    friday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    saturday: [],
    sunday: [],
  };
}

export function WeeklyScheduleEditor({
  gutachterId,
}: WeeklyScheduleEditorProps) {
  const existing = useQuery(api.availability.index.getWeeklySchedule, {
    gutachterId: gutachterId as never,
  });
  const setWeeklySchedule = useMutation(
    api.availability.index.setWeeklySchedule,
  );

  const [schedule, setSchedule] = useState<Schedule>(emptySchedule());
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Hydrate from server
  useEffect(() => {
    if (existing) {
      const parsed: Schedule = {} as Schedule;
      for (const key of DAY_KEYS) {
        try {
          parsed[key] = JSON.parse(existing[key] as string) as TimeWindow[];
        } catch {
          parsed[key] = [];
        }
      }
      setSchedule(parsed);
    }
  }, [existing]);

  const updateWindow = useCallback(
    (day: (typeof DAY_KEYS)[number], idx: number, field: "start" | "end", value: string) => {
      setSchedule((prev) => {
        const dayWindows = [...prev[day]];
        dayWindows[idx] = { ...dayWindows[idx], [field]: value };
        return { ...prev, [day]: dayWindows };
      });
      setSaved(false);
    },
    [],
  );

  const addWindow = useCallback((day: (typeof DAY_KEYS)[number]) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: [...prev[day], { start: "08:00", end: "18:00" }],
    }));
    setSaved(false);
  }, []);

  const removeWindow = useCallback(
    (day: (typeof DAY_KEYS)[number], idx: number) => {
      setSchedule((prev) => ({
        ...prev,
        [day]: prev[day].filter((_, i) => i !== idx),
      }));
      setSaved(false);
    },
    [],
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (const key of DAY_KEYS) {
        payload[key] = JSON.stringify(schedule[key]);
      }
      await setWeeklySchedule({
        gutachterId: gutachterId as never,
        ...(payload as Record<(typeof DAY_KEYS)[number], string>),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save schedule:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (existing === undefined) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {DAY_KEYS.map((day) => (
        <div
          key={day}
          className="rounded-lg border p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{DAY_LABELS[day]}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => addWindow(day)}
              className="h-7 gap-1 text-xs"
            >
              <Plus className="h-3 w-3" />
              Zeitfenster
            </Button>
          </div>

          {schedule[day].length === 0 ? (
            <p className="text-xs text-muted-foreground">Kein Zeitfenster (nicht verfügbar)</p>
          ) : (
            <div className="space-y-2">
              {schedule[day].map((window, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={window.start}
                    onChange={(e) =>
                      updateWindow(day, idx, "start", e.target.value)
                    }
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <span className="text-sm text-muted-foreground">bis</span>
                  <input
                    type="time"
                    value={window.end}
                    onChange={(e) =>
                      updateWindow(day, idx, "end", e.target.value)
                    }
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeWindow(day, idx)}
                    aria-label="Zeitfenster entfernen"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="flex items-center gap-3 pt-2">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Wochenplan speichern
        </Button>
        {saved && (
          <span className="text-sm text-green-600 dark:text-green-400">
            Gespeichert
          </span>
        )}
      </div>
    </div>
  );
}
